/**
 * ProjectViewSelector.jsx
 *
 * Tab selector for switching between different project views.
 * Uses MUI Tabs with i18n support and customizable styles.
 *
 * @component
 */

import PropTypes from "prop-types";
import { forwardRef, useCallback } from "react";
import { FormattedMessage } from "react-intl";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { default as ProjectViewUtils } from "./projectViewUtils";

/**
 * Creates a localized or fallback label for a tab.
 *
 * @param {Object} tab - Tab data object.
 * @param {string} [tab.name] - Optional display name for the tab.
 * @param {number} tab.type - Type used to derive translation key.
 * @param {number} tab.viewId - Unique identifier for the view.
 * @returns {React.ReactNode|string} - The rendered tab label.
 */
const createLabel = (tab) => {
  const { name, type, viewId } = tab;
  if (name) return name;
  const key = ProjectViewUtils.getTranslationKey(type);
  return key ? <FormattedMessage id={key} /> : `(${viewId})`;
};

/**
 * Returns a valid Tabs `value`.
 * MUI Tabs expects `false` if nothing is selected.
 *
 * @param {number|undefined} index - The currently selected tab index.
 * @returns {number|false} - Returns the index or false if undefined.
 */
const handleNoSelection = (index) => {
  if (typeof index === "number") {
    return index;
  }
  return false;
};

/**
 * ProjectViewSelector
 *
 * Renders a horizontally scrollable set of tabs for selecting different project views.
 * Supports i18n labels via FormattedMessage and allows custom styling through slotProps.
 * 
 * This component uses React.forwardRef to expose a ref to the root <Box> element,
 * enabling parent components to directly access or manipulate the container DOM node.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {function} [props.onSelect] - Callback when a tab is selected: (index, viewId) => void.
 * @param {number} [props.selectedIndex] - Currently selected tab index.
 * @param {Object} [props.slotProps] - Optional styling overrides for root, tabs, and tab elements.
 * @param {Array<Object>} props.tabsData - Array of tab data objects: { name, type, viewId, ariaControls }.
 * @param {React.Ref<HTMLDivElement>} ref - Forwarded ref to the root Box element.
 * @returns {JSX.Element} The rendered ProjectViewSelector component.
 */
const ProjectViewSelector = forwardRef(({
  onSelect,
  selectedIndex,
  slotProps = { root: {}, tab: {}, tabs: {}, },
  tabsData,
}, ref) => {
  /**
   * Handles tab selection changes.
   *
   * Invoked when a tab is selected or the active tab is clicked again.
   * Calls the `onSelect` callback with the index and viewId of the selected tab.
   * Ignores invalid or undefined indices, preventing errors when `tabsData` is incomplete.
   *
   * @param {number} index - Index of the tab that was selected.
   */
  const handleChange = useCallback(
    (index) => {
      if (!onSelect) return;
      const tab = tabsData?.[index];
      if (!tab) return;
      onSelect(index, tab.viewId);
    },
    [onSelect, tabsData]
  );

  /**
   * Handles clicks on the currently active tab.
   *
   * This allows triggering a re-selection action or refreshing the view
   * when the active tab is clicked again.
   * Delegates to `handleChange` for actual callback invocation.
   *
   * @param {number} index - Index of the clicked tab.
   */
  const handleTabClick = (index) => {
    if (index === selectedIndex) {
      handleChange(index);
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        ...slotProps?.root,
      }}
    >
      <Tabs
        role="tablist"
        aria-label="Project View Selector scrollable auto"
        onChange={(_, index) => handleChange(index)}
        scrollButtons="auto"
        sx={{
          "&.MuiTabs-root": {
            minHeight: "28px",
            "& .MuiTab-root": {
              fontSize: "12px",
              minHeight: "28px",
              px: 2,
              py: "1px",
              textTransform: "none",
            },
          },
          ...slotProps?.tabs,
        }}
        value={handleNoSelection(selectedIndex)}
        variant="scrollable"
      >
        {(tabsData || []).map((tab, index) => {
          const ariaProps = {
            "aria-controls": tab.ariaControls,
            id: ProjectViewUtils.getAriaTabId(tab.viewId),
          };
          return (
            <Tab
              role="tab"
              key={index}
              label={createLabel(tab)}
              onClick={() => handleTabClick(index)}
              sx={{ ...slotProps?.tab }}
              {...ariaProps}
            />
          );
        })}
      </Tabs>
    </Box>
  );
});

ProjectViewSelector.propTypes = {
  onSelect: PropTypes.func,
  selectedIndex: PropTypes.number,
  slotProps: PropTypes.shape({
    root: PropTypes.object,
    tabs: PropTypes.object,
    tab: PropTypes.object,
  }),
  tabsData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.number,
      viewId: PropTypes.number.isRequired,
      ariaControls: PropTypes.string,
    })
  ).isRequired,
};

ProjectViewSelector.displayName = "ProjectViewSelector";

export default ProjectViewSelector;
